import { PhotoType, type ClientOngoingLoad } from '@numeo/types-core'
import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { formatDate } from '../../results/format'
import { podSourceLabel } from './DocumentsSection'
import { DocumentCard, placeOf } from './DocumentCard'
import { InlineDocument } from './InlineDocument'

interface DocumentViewProps {
    /** The load's invoicing row, when it has one (where the POD came from, the check's mismatch sentence). */
    row?: TmsInvoiceRow
    loadNumber: string
    load: ClientOngoingLoad | undefined
}

/** When a stored file came in, as the source line says it ("Oct 26, 2026, 5:00 PM"), or null when unknown. */
const storedAt = (timestamp: Date | string | undefined): string | null => {
    if (!timestamp) return null
    const at = new Date(timestamp)
    if (!Number.isFinite(at.getTime())) return null
    return `${formatDate(at)}, ${at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
}

/**
 * The proof of delivery as the review drawer shows it (v17 `openDoc`): the document card with the reference the POD carries, the route,
 * the delivery and the receiver's signature as the automatic check read them, where it came from, and its files under it. Read only:
 * a change is made in the review, never here.
 */
export const DocumentView = ({ row, loadNumber, load }: DocumentViewProps) => {
    const files = (load?.photoProofs ?? []).filter((proof) => proof.type === PhotoType.POD && !proof.mergedIntoUrl)
    const check = load?.podCheck
    const mismatch = (row?.reasons ?? []).find((reason) => reason.code === 'reference_mismatch')
    const route = load?.route ?? []
    const delivery = route[route.length - 1]
    const newest = files.reduce<(typeof files)[number] | undefined>(
        (latest, file) => (!latest || new Date(file.timestamp ?? 0).getTime() > new Date(latest.timestamp ?? 0).getTime() ? file : latest),
        undefined
    )
    const source = files.length
        ? [podSourceLabel(row?.pod_source), storedAt(newest?.timestamp as Date | string | undefined)].filter(Boolean).join(' · ')
        : 'No proof of delivery file is stored for this load.'
    const reference = mismatch?.pod_reference ?? check?.poNumber ?? loadNumber
    return (
        <section className="flex flex-col" aria-label="Proof of delivery">
            <DocumentCard
                title="Proof of delivery"
                customer={row?.broker}
                fields={[
                    { label: 'Load reference', value: `#${reference}` },
                    { label: 'Origin', value: placeOf(route[0]) },
                    { label: 'Destination', value: placeOf(delivery) },
                    { label: 'Carrier', value: null },
                    { label: 'Delivery', value: delivery?.date ? formatDate(delivery.date) : null },
                    { label: 'Receiver signature', value: check ? (check.signed ? 'Present' : 'Not found') : null },
                ]}
                source={source}
            />
            {mismatch && <p className="mt-3 text-sm text-warning">{mismatch.message}</p>}
            {files.map((file, index) => (
                <div key={file.url} className="mt-4">
                    <InlineDocument url={file.url} title={`Proof of delivery ${files.length - index} for load #${loadNumber}`} />
                </div>
            ))}
        </section>
    )
}
